import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Header } from "./IndustriesPanel";

const FMT = new Intl.NumberFormat("en-BD");
const money = (n) => `৳${FMT.format(Math.round(n || 0))}`;

export default function PricingPanel() {
  const [tab, setTab] = useState("inquiries");
  return (
    <div data-testid="pricing-panel" className="space-y-6">
      <Header
        title="Pricing"
        subtitle="Inquiries submitted through the calculator + the raw calculation log."
      />
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "inquiries", label: "Inquiries" },
          { id: "logs", label: "Calculation log" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`pricing-subtab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              tab === t.id
                ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "inquiries" ? <Inquiries /> : <Logs />}
    </div>
  );
}

function Inquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing/inquiries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const contacted = items.filter((i) => i.contacted).length;
    return { total, contacted, open: total - contacted };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "new") return items.filter((l) => !l.contacted);
    if (filter === "contacted") return items.filter((l) => l.contacted);
    return items;
  }, [items, filter]);

  const toggleContacted = async (inq, value) => {
    setItems((p) => p.map((l) => (l.id === inq.id ? { ...l, contacted: value } : l)));
    try {
      const { data } = await api.patch(`/admin/pricing/inquiries/${inq.id}`, { contacted: value });
      setItems((p) => p.map((l) => (l.id === inq.id ? { ...l, ...data } : l)));
      toast.success(value ? "Marked as contacted." : "Marked as not contacted.");
    } catch (e) {
      toast.error(formatApiError(e));
      setItems((p) => p.map((l) => (l.id === inq.id ? { ...l, contacted: !value } : l)));
    }
  };

  const saveNotes = async (inq, notes) => {
    if ((notes || "") === (inq.notes || "")) return;
    try {
      const { data } = await api.patch(`/admin/pricing/inquiries/${inq.id}`, { notes });
      setItems((p) => p.map((l) => (l.id === inq.id ? { ...l, ...data } : l)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (inq) => {
    if (!window.confirm(`Delete inquiry from ${inq.email}?`)) return;
    try {
      await api.delete(`/admin/pricing/inquiries/${inq.id}`);
      setItems((p) => p.filter((l) => l.id !== inq.id));
      toast.success("Inquiry deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "all", label: `All · ${stats.total}` },
          { id: "new", label: `Not contacted · ${stats.open}` },
          { id: "contacted", label: `Contacted · ${stats.contacted}` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`inquiries-filter-${t.id}`}
            onClick={() => setFilter(t.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              filter === t.id ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
        {loading && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-sm text-neutral-500">
            {items.length === 0 ? "No inquiries yet." : "Nothing matches this filter."}
          </div>
        )}
        {filtered.map((inq) => (
          <div
            key={inq.id}
            data-testid={`inquiry-row-${inq.id}`}
            className={`p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 ${inq.contacted ? "bg-neutral-50/50" : ""}`}
          >
            <div className="lg:col-span-4">
              <div className="font-display font-bold text-neutral-950">{inq.name}</div>
              <a href={`mailto:${inq.email}`} className="text-sm text-neutral-900 underline underline-offset-2">{inq.email}</a>
              <div className="mt-1 text-xs text-neutral-500">
                {inq.company || "-"}{inq.phone ? ` · ${inq.phone}` : ""}
              </div>
              <div className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                {(inq.created_at || "").slice(0, 19).replace("T", " ")}
              </div>
            </div>

            <div className="lg:col-span-4 text-xs text-neutral-700 space-y-1.5">
              <div><b className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">Cameras / Features:</b> {inq.cameras} cam · {inq.features} feat</div>
              <div><b className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">Existing cameras / GPU servers:</b> {inq.has_existing_cameras ? "Yes" : "No"} / {inq.has_gpu_server ? "Yes" : "No"}</div>
              <div><b className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">Monthly:</b> {money(inq.breakdown?.monthly_total)} · <b className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">Install:</b> {money(inq.breakdown?.oneoff_cameras)}</div>
              {inq.custom_requirement && (
                <div className="mt-1 p-2 bg-neutral-50 rounded-md border border-neutral-200">
                  <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">Custom requirement</div>
                  <div className="text-neutral-700 mt-0.5 whitespace-pre-wrap">{inq.custom_requirement}</div>
                </div>
              )}
              {inq.message && (
                <div className="mt-1 text-neutral-600 italic">{inq.message}</div>
              )}
            </div>

            <div className="lg:col-span-4 space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid={`inquiry-contacted-${inq.id}`}
                  checked={Boolean(inq.contacted)}
                  onChange={(e) => toggleContacted(inq, e.target.checked)}
                  className="w-4 h-4 accent-[#84CC16]"
                />
                <span className={`text-xs font-semibold ${inq.contacted ? "text-[#65A30D]" : "text-neutral-600"}`}>
                  {inq.contacted ? "Contacted" : "Not contacted"}
                </span>
              </label>
              <input
                type="text"
                data-testid={`inquiry-notes-${inq.id}`}
                defaultValue={inq.notes || ""}
                placeholder="Internal notes…"
                onBlur={(e) => saveNotes(inq, e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-md border border-neutral-200 focus:border-[#84CC16] focus:outline-none"
              />
              <button
                type="button"
                data-testid={`inquiry-delete-${inq.id}`}
                onClick={() => remove(inq)}
                className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Logs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing/calculations", { params: { limit: 1000 } });
      setItems(data);
      setPage(1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageItems = items.slice(start, start + PAGE_SIZE);

  return (
    <div className="space-y-3">
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <Th className="w-14">S.No</Th>
              <Th>When</Th>
              <Th>Cameras</Th>
              <Th>Features</Th>
              <Th>Existing</Th>
              <Th>GPU</Th>
              <Th>Monthly</Th>
              <Th>One-off</Th>
              <Th>Session</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-4 text-sm text-neutral-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="p-6 text-sm text-neutral-500">No logs yet - users haven&apos;t tried the calculator yet.</td></tr>}
            {pageItems.map((l, idx) => (
              <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-b border-neutral-100 last:border-0">
                <Td className="font-mono-ui text-[11px] text-neutral-500">{start + idx + 1}</Td>
                <Td className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-600">
                  {(l.created_at || "").slice(0, 19).replace("T", " ")}
                </Td>
                <Td>{l.cameras}</Td>
                <Td>{l.features}</Td>
                <Td>{l.has_existing_cameras ? "Yes" : "No"}</Td>
                <Td>{l.has_gpu_server ? "Yes" : "No"}</Td>
                <Td className="font-semibold">{money(l.breakdown?.monthly_total)}</Td>
                <Td>{money(l.breakdown?.oneoff_cameras)}</Td>
                <Td className="font-mono-ui text-[9px] text-neutral-400">{(l.session_id || "-").slice(0, 14)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="logs-pagination">
          <p className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-500">
            Showing {items.length === 0 ? 0 : start + 1}–{Math.min(start + PAGE_SIZE, items.length)} of {items.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="logs-page-prev"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="text-xs font-semibold px-3 py-1.5 rounded-md border border-neutral-200 text-neutral-700 hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span data-testid="logs-page-indicator" className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-neutral-700 px-3">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              data-testid="logs-page-next"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="text-xs font-semibold px-3 py-1.5 rounded-md border border-neutral-200 text-neutral-700 hover:border-neutral-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 px-3 py-2">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
